import React from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { HomesItinerary } from "redmond";
import { useDeviceTypes } from "halifax";
import "./styles.scss";

export interface IViewHostAndRentalDetailsModalContentProps {
  home: HomesItinerary;
}

const ViewHostAndRentalDetailsModalContent = ({
  home,
}: IViewHostAndRentalDetailsModalContentProps) => {
  const { matchesMobile } = useDeviceTypes();

  const { reservation } = home;
  const { contacts } = reservation.hostInfo || {};
  const { access, internet, additional } = reservation.stayInformation || {};

  return (
    <Box padding={matchesMobile ? "25px" : "50px"}>
      <Typography variant="h2" style={{ paddingBottom: "15px" }}>
        Contact Host
      </Typography>
      <Typography variant="body2" style={{ paddingBottom: "30px" }}>
        Get in touch with your host regarding check-in, checkout, etc.
      </Typography>

      {contacts && contacts.length > 0 && (
        <Box
          display="flex"
          flexDirection="column"
          style={{ gap: "30px" }}
          paddingBottom={"30px"}
        >
          {contacts.map((contact) => {
            return (
              <Box
                key={contact.type}
                display="flex"
                flexDirection="column"
                style={{ gap: "15px" }}
              >
                <Typography variant="h4">
                  {contact.type}
                  {contact?.name && ` - ${contact.name}`}
                </Typography>

                {contact.phoneNumber && (
                  <TitleAndDescription
                    title={"Phone Number"}
                    description={contact.phoneNumber}
                  />
                )}

                {contact.email && (
                  <Box>
                    <Typography style={{ fontWeight: 600 }}>Email</Typography>
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        style={{ textDecoration: "none" }}
                      >
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 600,
                            color: "#013D58",
                          }}
                        >
                          {contact.email}
                        </Typography>
                      </a>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {(access || additional || internet) && (
        <>
          <Box paddingBottom={"30px"}>
            <Divider />
          </Box>

          <Typography
            className="title"
            variant="h2"
            style={{ paddingBottom: "15px" }}
          >
            Essentials
          </Typography>
          <Box style={{ gap: "30px" }}>
            {access && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{ gap: "15px" }}
                >
                  {access?.instructions && (
                    <TitleAndDescription
                      title={"Access"}
                      description={access.instructions}
                    />
                  )}
                  {access?.accessCode && (
                    <TitleAndDescription
                      title={"Access Code"}
                      description={access?.accessCode}
                    />
                  )}
                  {access?.accessType && (
                    <TitleAndDescription
                      title={"Access Type"}
                      description={access?.accessType}
                    />
                  )}

                  {access?.imageUrls && access.imageUrls.length > 0 && (
                    <Images imageUrls={access.imageUrls} />
                  )}
                </Box>
                <Box paddingY={"30px"}>
                  <Divider />
                </Box>
              </>
            )}

            {additional && additional.length > 0 && (
              <>
                <Box
                  display="flex"
                  flexDirection="column"
                  style={{ gap: "15px" }}
                >
                  {additional.map((content) => {
                    if (!content.topic) return null;

                    return (
                      <>
                        {content.topic && (
                          <TitleAndDescription
                            key={content.topic}
                            title={content.topic}
                            description={content.instructions}
                          />
                        )}

                        {content?.imageUrls && content.imageUrls.length > 0 && (
                          <Images imageUrls={content.imageUrls} />
                        )}
                      </>
                    );
                  })}
                </Box>
                <Box paddingY={"30px"}>
                  <Divider />
                </Box>
              </>
            )}

            {internet && (
              <Box
                display="flex"
                flexDirection="column"
                style={{ gap: "15px" }}
              >
                {internet?.instructions && (
                  <TitleAndDescription
                    title={"Wifi"}
                    description={internet.instructions}
                  />
                )}
                {internet?.networkName && (
                  <TitleAndDescription
                    title={"Network Name"}
                    description={internet.networkName}
                  />
                )}
                {internet?.networkPassword && (
                  <TitleAndDescription
                    title={"Network Password"}
                    description={internet.networkPassword}
                  />
                )}

                {internet?.imageUrls && internet.imageUrls.length > 0 && (
                  <Images imageUrls={internet.imageUrls} />
                )}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

interface TitleAndDescriptionProps {
  title: string;
  description?: string;
}
const TitleAndDescription: React.FC<TitleAndDescriptionProps> = ({
  title,
  description,
}) => {
  return (
    <Box>
      <Typography style={{ fontWeight: 600 }}>{title}</Typography>
      {description && <Typography variant="body2">{description}</Typography>}
    </Box>
  );
};

interface ImagesProps {
  imageUrls: string[];
}

const Images = ({ imageUrls }: ImagesProps) => {
  const { matchesMobile } = useDeviceTypes();

  return (
    <Box display="flex" flexWrap="wrap" style={{ gap: "15px" }}>
      {imageUrls.map((url) => {
        return (
          <Box
            key={url}
            height="350px"
            maxWidth={matchesMobile ? "100%" : "50%"}
            flex={matchesMobile ? "1 1 100%" : "1 1 0%"}
            style={{
              backgroundImage: `url("${url}")`,
              backgroundColor: "#ccc" /* fallback color */,
              borderRadius: "10px",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />
        );
      })}
    </Box>
  );
};

export default ViewHostAndRentalDetailsModalContent;
